<template>
  <div
    v-if="checked"
    class="flex flex-col min-h-screen items-center"
  >
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="ml-4 sm:ml-0">
        <LogoIcon />
      </div>
      <div class="h-12 mt-5 mb-5 mr-4 sm:mr-8">
        <LanguageSwitch />
      </div>
    </div>
    <div
      v-if="auth_type === 0"
      class="max-w-[460px] w-full text-center flex-grow px-4"
    >
      <h2 class="mt-5 mt-10 mb-6 text-center">
        {{ $t('messages.two_factor_auth.title') }}
      </h2>
      <p class="b1 block text-left text-zyxel-gray-800 mb-8">
        {{ $t('messages.two_factor_auth.otp_description') }}
      </p>
      <label
        for="code"
        class="b2 block text-left w-full mb-2 text-zyxel-gray-800"
      >{{ $t('messages.text_input.code') }}</label>
      <input
        id="code"
        ref="codeInput"
        v-model="code"
        type="text"
        inputmode="numeric"
        :class="['text-input pt-4 pl-4 pb-4 text-left h-12 w-full', { 'error-input': errors.code }]"
        @keyup.enter="handleOTPVerify"
      >
      <span
        v-if="errors.code"
        class="error-message mt-1 b2 block text-left w-full"
        aria-live="assertive"
      >{{
        $t(errors_msg.code) }}</span>
      <button
        class="button-primary w-full mt-4 mb-3"
        @click="handleOTPVerify"
      >
        {{ $t('messages.button.verify')
        }}
      </button>
      <div class="mt-4 mb-6">
        <span
          class="mr-4 b1 text-zyxel-gray-800"
          style="color: #575757"
        >{{
          $t('messages.common.hint_having_trouble') }}</span>
        <button
          class="b1 text-zyxel-green-700 cursor-pointer"
          @click="DisableGoogle2FA"
        >
          {{ $t('messages.common.link_get_help') }}
        </button>
      </div>
    </div>
    <div
      v-else-if="auth_type === 999"
      class="max-w-[460px] w-full text-center flex-grow px-4"
    >
      <h2 class="mt-5 mt-10 mb-6 text-center">
        {{ $t('messages.two_factor_auth.redirecting') }}
      </h2>
    </div>
    <div
      v-else
      class="max-w-[460px] w-full text-center flex-grow px-4"
    >
      <h2 class="mt-5 mt-10 mb-6 text-center">
        {{ $t('messages.two_factor_auth.title') }}
      </h2>
      <div class="bg-white rounded-lg">
        <div class="flex mb-4 text-left">
          <img
            class="ml-6 mt-6 h-12 w-12"
            src="@/assets/svg/icon_48px_send.svg"
            alt="Send email icon"
          >
          <div class="ml-4 mt-6 mr-6">
            <span class="b1 text-zyxel-gray-800 font-bold">{{ $t('messages.common.hint_send_email_to')
            }}</span><br>
            <span class="b1 text-zyxel-gray-800 font-bold break-all whitespace-normal">{{ toEmail }}</span>
          </div>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-8 pb-6">
          {{
            $t('messages.two_factor_auth.email_description') }}
        </p>
      </div>
      <div class="mt-4 mb-6 flex justify-center items-center">
        <span
          class="mr-4 b1 text-zyxel-gray-800"
          style="color: #575757"
        >{{
          $t('messages.common.hint_not_receive_yet') }}</span>
        <div
          v-if="loading"
          class="loader ml-4"
        />
        <div
          v-else
          class="ml-4"
        >
          <button
            class="b1 text-zyxel-green-700 cursor-pointer"
            @click="sendEmail2FA"
          >
            {{ $t("messages.common.link_send_again") }}
          </button>
        </div>
      </div>
    </div>
    <ZyxelFooter />
  </div>
</template>
<script>
import LogoIcon from '@/components/LogoIcon.vue';
import LanguageSwitch from "@/components/LanguageSwitch.vue";
import ZyxelFooter from "@/components/ZyxelFooter.vue";
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';


export default {
    name: 'TwoFactorAuthVerifyPage',
    components: {
        LogoIcon,
        LanguageSwitch,
        ZyxelFooter,
    },
    data() {
        return {
            auth_type: 0,
            toEmail: "",
            code: '',
            checked: false,
            loading: false,
            errors: {
                code: false,
            },
            errors_msg: {
                code: false,
            }
        };
    },
    mounted() {
      document.addEventListener('keydown', this.handleKeydown);
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.handleKeydown);
    },
    created() {
        this.checkUriAndExecute();
    },
    methods: {
        handleKeydown() {
            if (this.$refs.codeInput && document.activeElement !== this.$refs.codeInput) {
                this.$refs.codeInput.focus();
                document.removeEventListener('keydown', this.handleKeydown);
            }
        },
        async checkUriAndExecute() {
            if (this.$route.path === '/2fa/email') {
                const queryParams = this.$route.query;
                this.toEmail = queryParams.to_email;
                if(this.toEmail){
                    this.auth_type = 1;
                }
                else{
                    //The magic number 999 indicates that the UI should display 'Redirecting...'
                    this.auth_type = 999;
                    try {
                        let response;
                        const login_time = new Date().toString();  // local time
                        if (queryParams.pre === 'enable' && queryParams.token) {
                            response = await axios.post(`${apiBaseUrl}/api/users/2fa/email/enable`, { token: queryParams.token, login_time: login_time });
                        } else if (queryParams.pre === 'disable' && queryParams.token) {
                            response = await axios.post(`${apiBaseUrl}/api/users/2fa/disable`, { token: queryParams.token, login_time: login_time });
                        } else if (queryParams.pre === 'signin' && queryParams.token) {
                            response = await axios.post(`${apiBaseUrl}/api/users/2fa/email/signin`, { token: queryParams.token, login_time: login_time });
                        } else {
                            // If none of the conditions are met, return or handle as needed
                            this.checked = true;
                            return;
                        }

                        const auth = useAuthStore();
                        await auth.authenticateUser(response.data.access_token);

                        // Additional logic for 'signin'
                        if (queryParams.pre === 'signin') {
                            //this flow must to take care redirect
                            auth.getAuthorizationCodeAndRedirect();
                        } else if (queryParams.pre === 'enable') {
                            this.$router.push({ name: 'TwoFactorAuthPage' , query: { enableEmail: true }});
                        }else {
                            this.$router.push({ name: 'TwoFactorAuthPage' , query: { enableEmail: false }});
                        }

                    } catch (error) {
                        logger.log(error);
                        this.$router.push({ name: 'SignIn' });
                    }
                }
            }
            this.checked = true;
        },
        async handleOTPVerify() {
            if (!this.code) {
                this.errors.code = true;
                this.errors_msg.code = "messages.error_msg.otp_verify_failed";
                return;
            }
            this.errors.code = false;

            try {
                const login_time = new Date().toString();  // local time
                const response = await axios.post(`${apiBaseUrl}/api/users/2fa/otp/signin`, { pincode: this.code, login_time: login_time });

                if (response.status === 200 && response.data.access_token) {
                    const auth = useAuthStore();
                    await auth.authenticateUser(response.data.access_token);
                    auth.getAuthorizationCodeAndRedirect();
                } else {
                    this.errors.code = true;
                    this.errors_msg.code = "messages.error_msg.otp_verify_failed";
                }
            } catch (error) {
                logger.log(error);
                this.errors.code = true;
                this.errors_msg.code = "messages.error_msg.otp_verify_failed";
            }
        },
        async sendEmail2FA() {
            this.loading = true;
            const auth = useAuthStore();
            await auth.send2FASigninEmail();
            setTimeout(() => {
                this.loading = false;
            }, 2000);
        },
        async DisableGoogle2FA() {
            const authStore = useAuthStore();
            const result = await authStore.disable2FA();
            if (result) {
                const email = authStore.current_user.email;
                this.$router.push({ name: 'WaitDisable2FA', query: { to_email: email } });
            }
        }
    },
}
</script>